import type { CoreError } from '@ingenico/api-sdk-core/common';
import { getDeviceCallLogsRequestBody, useBusinessServiceRequest } from '@ingenico/react-section-utils';
import { isEmpty, toString } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router';
import { useParameterOutputListModal } from '../../components/parameter-output-list-modal/useParameterOutputListModal.hook';
import type { IUseProvisioningDeviceChangeDetailsPageVMProps } from './provisioningDeviceChangeDetailsPage.model';
import {
  useGenerateTargetDeviceGeneralItems,
  useGenerateTargetDeviceIdItems,
} from './useProvisioningDeviceChangeDetailsPage.hook';
import { useDeviceManagementCapability, useDeviceManagementError } from '../../hooks';
import { DeviceManagementService } from '../../domain/business/services/deviceManagement.service';
import { getDeviceChangesMapperOfStatus } from '../../utils/deviceChangesDetails.util';
import type { UseDeviceChangeDetailsPageReturn } from '../../model/deviceChangeDetailsPage.model';
import { useGenerateProvisioningTargetDeviceCallLogs } from '../../hooks/useGenerateProvisioningTargetDeviceCallLogs.hook';

export function useProvisioningDeviceChangeDetailsPageVM({
  t,
}: IUseProvisioningDeviceChangeDetailsPageVMProps): UseDeviceChangeDetailsPageReturn {
  const { deviceChangesId } = useParams<{
    deviceChangesId: string;
  }>();
  const { getErrorMessage } = useDeviceManagementError();

  const deviceChangesByIdQuery = useBusinessServiceRequest(
    DeviceManagementService,
    useCallback(
      (service) =>
        isEmpty(toString(deviceChangesId)) ? null : service.getDeviceChangesById(toString(deviceChangesId)),
      [deviceChangesId],
    ),
  );

  const deviceCallQuery = useBusinessServiceRequest(
    DeviceManagementService,
    useCallback(
      (service) =>
        isEmpty(deviceChangesByIdQuery?.data?.logicalDevice?.id) || isEmpty(deviceChangesId)
          ? null
          : service.getSearchDeviceCallStatus({
              deviceId: toString(deviceChangesByIdQuery?.data?.logicalDevice?.id),
              body: getDeviceCallLogsRequestBody({ deviceId: toString(deviceChangesId) }),
            }),
      [deviceChangesByIdQuery?.data?.logicalDevice?.id, deviceChangesId],
    ),
  );

  const targetDeviceIdItem = useGenerateTargetDeviceIdItems(t, deviceChangesByIdQuery);
  const targetDeviceGeneralItems = useGenerateTargetDeviceGeneralItems(t, deviceChangesByIdQuery);
  const deviceCallLogs = useGenerateProvisioningTargetDeviceCallLogs(
    deviceCallQuery,
    toString(deviceChangesByIdQuery?.data?.logicalDevice?.id),
  );

  const mapperOfStatus = useMemo(() => getDeviceChangesMapperOfStatus(), []);
  const statusData = mapperOfStatus[deviceChangesByIdQuery?.data?.status ?? ''];

  const errorAPI: CoreError = useMemo(
    () => ({
      data: {
        type: toString(deviceChangesByIdQuery?.data?.statusDetail),
        title: toString(deviceChangesByIdQuery?.data?.description),
      },
    }),
    [deviceChangesByIdQuery],
  );

  const errorMessage = getErrorMessage(errorAPI);

  const canFetchOutputfiles = useMemo(() => {
    const status = deviceChangesByIdQuery?.data?.status;
    if (status) {
      return !(status === 'CREATED' || status === 'CANCELED');
    }
    return false;
  }, [deviceChangesByIdQuery?.data?.status]);

  const outputFilenamesQuery = useBusinessServiceRequest(
    DeviceManagementService,
    useCallback(
      (service) => {
        return canFetchOutputfiles ? service.getOutputFilenamesForDeploymentUnit(toString(deviceChangesId)) : null;
      },
      [canFetchOutputfiles, deviceChangesId],
    ),
  );

  const canViewOutputFile = useDeviceManagementCapability('logicalDevice.canViewOutputFile');

  const canShowParameterOutputSection = useMemo(() => {
    if (!canViewOutputFile) {
      return false;
    }

    const status = deviceChangesByIdQuery?.data?.status;
    // If the deployment unit status is CREATED or CANCELED, don't have an output file.
    if (status === 'CREATED' || status === 'CANCELED') {
      return false;
    }

    //Any other status check if there are files
    const files = outputFilenamesQuery?.data?.data;
    if (files) {
      return Array.isArray(files) && files.length > 0;
    }
    return false;
  }, [canViewOutputFile, deviceChangesByIdQuery?.data?.status, outputFilenamesQuery?.data?.data]);

  const snapshotId = useMemo(() => {
    return toString(deviceChangesByIdQuery?.data?.options?.configuration?.snapshotId);
  }, [deviceChangesByIdQuery?.data?.options?.configuration?.snapshotId]);
  const viewOutputListItems = useBusinessServiceRequest(
    DeviceManagementService,
    useCallback(
      (provisioningService) => (snapshotId ? provisioningService.getSnapshotListForViewOutput(snapshotId) : null),
      [snapshotId],
    ),
  );

  const {
    data: viewOutputList,
    isLoading: isLoadingViewOutputList,
    isError: isErrorViewOutputList,
    error: errorViewOutputList,
  } = viewOutputListItems;
  const { parameterOutputListModal, handleParameterOutputListModalOpen: _handleParameterOutputListModalOpen } =
    useParameterOutputListModal({
      data: viewOutputList,
      isLoading: isLoadingViewOutputList,
      isError: isErrorViewOutputList,
      error: errorViewOutputList,
      deploymentUnitId: toString(deviceChangesId),
    });

  return {
    deviceChangesByIdQuery,
    statusData,
    targetDeviceIdItem,
    targetDeviceGeneralItems,
    errorMessage,
    deviceCallQuery,
    deviceCallLogs,
    parameterOutputListModal,
    canShowParameterOutputSection,
  };
}
